/**
 * Plotly trace + layout builders for the admin analytics pages. Pure
 * functions — they take API rows and return finished `{ traces, layout }`
 * pairs, so the page components only have to compose and render.
 */
import type { Data, Layout } from 'plotly.js';
import { chartLayout } from '@/lib/plotly/layoutPresets';

export interface ChartSpec {
  traces: Data[];
  layout: Partial<Layout>;
}

/** Transparent background — admin charts sit inside a white `<Card>`. */
const TRANSPARENT_BG = {
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
} as const;

/**
 * Daily-activity chart for the User Activity page: events bars on the left
 * axis, unique-users and doc-views lines on a secondary right axis.
 */
export function buildActivityDailyChart(
  points: { date: string; total_events: number; unique_users: number; document_views: number }[],
): ChartSpec {
  const dates = points.map((p) => p.date);
  const traces: Data[] = [
    {
      x: dates,
      y: points.map((p) => p.total_events),
      type: 'bar',
      name: 'Total Events',
      marker: { color: '#4EB7AC' },
    },
    {
      x: dates,
      y: points.map((p) => p.unique_users),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Unique Users',
      line: { color: '#1D3A47', width: 2 },
      marker: { size: 6 },
      yaxis: 'y2',
    },
    {
      x: dates,
      y: points.map((p) => p.document_views),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Doc Views',
      line: { color: '#f59e0b', width: 2 },
      marker: { size: 5 },
      yaxis: 'y2',
    },
  ];
  return {
    traces,
    layout: dualAxisLayout('Total Events', 'Users / Doc Views'),
  };
}

/**
 * Daily-logins chart for the Login Analytics page: login bars on the left
 * axis, unique-users line on a secondary right axis.
 */
export function buildLoginDailyChart(
  points: { date: string; total_logins: number; unique_users: number }[],
): ChartSpec {
  const dates = points.map((p) => p.date);
  const traces: Data[] = [
    {
      x: dates,
      y: points.map((p) => p.total_logins),
      type: 'bar',
      name: 'Total Logins',
      marker: { color: '#4EB7AC' },
    },
    {
      x: dates,
      y: points.map((p) => p.unique_users),
      type: 'scatter',
      mode: 'lines+markers',
      name: 'Unique Users',
      line: { color: '#1D3A47', width: 2 },
      marker: { size: 6 },
      yaxis: 'y2',
    },
  ];
  return {
    traces,
    layout: dualAxisLayout('Total Logins', 'Unique Users'),
  };
}

/**
 * Shared layout for the two daily charts: grouped bars + date x-axis + a
 * secondary right y-axis. The chart title lives on the wrapping `<Card>`, so
 * none is set here. The wider right margin (60) leaves room for the secondary
 * axis.
 */
function dualAxisLayout(leftAxisLabel: string, rightAxisLabel: string): Partial<Layout> {
  const preset = chartLayout({ family: 'bar', marginOverride: { t: 50, r: 60, b: 50, l: 60 } });
  return {
    ...preset,
    // Legacy `admin/user-activity.js` leaves this legend at the Plotly default
    // 12; pin it so the shared default (now 16) does not enlarge it.
    legend: {
      ...(preset.legend as object),
      font: { ...((preset.legend as { font?: object })?.font ?? {}), size: 12 },
    },
    barmode: 'group',
    bargap: 0.3,
    xaxis: {
      ...preset.xaxis,
      type: 'date',
      tickformat: '%b %d',
      tickangle: -45,
    },
    yaxis: {
      ...preset.yaxis,
      title: { text: leftAxisLabel, standoff: 10 },
      side: 'left',
      rangemode: 'tozero',
    },
    yaxis2: {
      title: { text: rightAxisLabel },
      side: 'right',
      overlaying: 'y',
      rangemode: 'tozero',
    },
    ...TRANSPARENT_BG,
  };
}

/**
 * Single-series horizontal bar chart used for the "Top Pages" / "Top Events"
 * panels. Rows are reversed so the largest value renders at the top (Plotly
 * draws horizontal bars bottom-to-top). The title lives on the wrapping
 * `<Card>`; the wide left margin (160) leaves room for long y-tick labels.
 */
export function buildHBar<T>(
  rows: T[],
  getLabel: (r: T) => string,
  getValue: (r: T) => number,
  xAxisLabel: string,
  color: string,
): ChartSpec {
  const ordered = [...rows].reverse();
  const traces: Data[] = [
    {
      x: ordered.map(getValue),
      y: ordered.map(getLabel),
      type: 'bar',
      orientation: 'h',
      marker: { color },
    },
  ];
  const preset = chartLayout({
    family: 'bar',
    showLegend: false,
    marginOverride: { t: 30, r: 20, b: 50, l: 160 },
  });
  return {
    traces,
    layout: {
      ...preset,
      barmode: 'group',
      xaxis: {
        ...preset.xaxis,
        title: { text: xAxisLabel, standoff: 10 },
      },
      yaxis: { ...preset.yaxis },
      ...TRANSPARENT_BG,
    },
  };
}
